import ProtectionLawLogo from "../../assets/trusted-licensed/nhra.png";
import PersonalDataProtectionIcon from "../../assets/trusted-licensed/personal-data-protection.png";
import { useTranslation } from "react-i18next";
import { useLocal } from "hooks/useLocal";

type TrustedLicenseProps = {
  // Page-specific privacy copy, e.g. the school page's parent-consent note.
  privacyDescription?: string;
};

export function TrustedLicense({ privacyDescription }: TrustedLicenseProps = {}) {
  const { t } = useTranslation(["common"]);
  const { direction, isArabic } = useLocal();
  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  return (
    <section
      dir={dirAttr}
      className={`bg-white py-10 ${isArabic ? "font-ibmplex" : "font-inter"} px-6`}
    >
      <article className="py-10 md:px-[80.5px] text-white max-w-[1350px] mx-auto bg-[#4242CE] rounded-2xl flex flex-col md:flex-row justify-center md:justify-between items-center gap-y-10 md:gap-y-0 md:gap-[80px] max-md:px-6">
        {/* left section */}
        <article className="flex flex-col gap-2 w-full md:max-w-[517px]">
          <p
            className="text-2xl max-md:text-xl font-bold leading-[29px]"
            dir={dirAttr}
          >
            {t("trustedLicensed.title")}
          </p>
          <p
            className="text-[#CECECE] text-[14px] md:text-[16px] leading-[19px]"
            dir={dirAttr}
          >
            {t("trustedLicensed.description")}
          </p>
          <article className="flex gap-4 items-start md:items-center mt-8">
            <svg
              width="42"
              height="42"
              viewBox="0 0 42 42"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <rect width="42" height="42" rx="21" fill="#6C6AD8" />
              <path
                d="M16.8333 20.167V16.8337C16.8333 15.7286 17.2723 14.6688 18.0537 13.8874C18.8351 13.106 19.8949 12.667 21 12.667C22.1051 12.667 23.1649 13.106 23.9463 13.8874C24.7277 14.6688 25.1667 15.7286 25.1667 16.8337V20.167M15.1667 20.167H26.8333C27.7538 20.167 28.5 20.9132 28.5 21.8337V27.667C28.5 28.5875 27.7538 29.3337 26.8333 29.3337H15.1667C14.2462 29.3337 13.5 28.5875 13.5 27.667V21.8337C13.5 20.9132 14.2462 20.167 15.1667 20.167Z"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold leading-[17px]">
                {t("trustedLicensed.privacyTitle")}
              </p>
              <p className="text-xs text-[#CECECE]">
                {privacyDescription ?? t("trustedLicensed.privacyDescription")}
              </p>
            </div>
          </article>
          <article className="flex gap-4 items-start md:items-center mt-6">
            <svg
              width="42"
              height="42"
              viewBox="0 0 42 42"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <rect width="42" height="42" rx="21" fill="#6C6AD8" />
              <g clip-path="url(#clip0_268_950)">
                <path
                  d="M20.9997 29.3337C20.9997 29.3337 27.6663 26.0003 27.6663 21.0003V15.167L20.9997 12.667L14.333 15.167V21.0003C14.333 26.0003 20.9997 29.3337 20.9997 29.3337Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_268_950">
                  <rect
                    width="20"
                    height="20"
                    fill="white"
                    transform="translate(11 11)"
                  />
                </clipPath>
              </defs>
            </svg>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold leading-[17px]">
                {t("trustedLicensed.shieldTitle")}
              </p>
              <p className="text-xs text-[#CECECE]">
                {t("trustedLicensed.shieldDescription")}
              </p>
            </div>
          </article>
        </article>

        {/* right section */}
        <section className="flex gap-6 w-full md:w-auto justify-center">
          <article className="bg-[#5A58D2] rounded-xl py-6 px-[13.5px] flex-1 md:flex-none md:max-w-[169px] flex flex-col justify-center items-center">
            <img src={ProtectionLawLogo} alt="NHRA Licensed" className="mb-6" />
            <p className="font-semibold text-[10px] md:text-sm text-center leading-[110%]">
              {t("trustedLicensed.nhraTitle")}
            </p>
            <p className="text-xs text-[#CECECE] mt-1 max-md:text-center max-md:text-[10px]">
              {t("trustedLicensed.nhraSubtitle")}
            </p>
          </article>
          <article className="bg-[#5A58D2] rounded-xl py-6 px-[13.5px] flex-1 md:flex-none md:max-w-[169px] flex flex-col justify-center items-center">
            <img
              src={PersonalDataProtectionIcon}
              alt="Personal Data Protection"
              className="mb-6"
            />
            <p className="font-semibold text-sm text-center max-md:text-[10px] leading-[110%]">
              {t("trustedLicensed.pdplTitle")}
            </p>
            <p className="text-xs text-[#CECECE] mt-1 max-md:text-[10px] max-md:text-center">
              {t("trustedLicensed.pdplSubtitle")}
            </p>
          </article>
        </section>
      </article>
    </section>
  );
}
